import { apiKeyClient } from "@better-auth/api-key/client";
import { ssoClient } from "@better-auth/sso/client";
import { env } from "@repo/env";
import {
  adminClient,
  lastLoginMethodClient,
  magicLinkClient,
  oneTapClient,
  organizationClient,
  twoFactorClient,
  usernameClient,
} from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";

import { safeInternalPath } from "@/lib/safe-redirect";
import { clearViewAs } from "@/types/session";

export const googleOneTapClientId = env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

export const authClient = createAuthClient({
  // Use empty string for relative URLs - this ensures auth calls go to the current origin
  // This is critical for Vercel preview deployments to avoid CORS issues
  baseURL:
    typeof window !== "undefined"
      ? "" // Client-side: use relative URLs (current origin)
      : env.NEXT_PUBLIC_APP_URL || "http://localhost:3000", // SSR: use env var
  // Better Auth refetches /get-session on every tab focus by default, so
  // power users juggling tabs generate a steady stream of session requests
  // that competes with real traffic for the origin's rate-limit budget (a
  // top contributor to the edge 429s). Turn it off to match the app-wide
  // TanStack policy (refetchOnWindowFocus: false, see tanstack-query.tsx).
  // The session still loads on mount and on sign-in/out (cross-tab broadcast),
  // refreshes on network reconnect, and is validated server-side per request.
  //
  // This request is the only one that extends a session with a cookie the
  // browser keeps (see `session-refresh-guard.ts`), so a tab left open for a
  // week still has to make one before the cookie's seven days run out.
  sessionOptions: {
    refetchInterval: 12 * 60 * 60,
    refetchOnWindowFocus: false,
  },
  plugins: [
    // No `teams` option: the server plugin does not enable teams either, so the
    // nine team endpoints it would advertise here do not exist.
    organizationClient(),
    adminClient(),
    usernameClient(),
    twoFactorClient({
      onTwoFactorRedirect() {
        const stored = sessionStorage.getItem("postLoginCallback");
        sessionStorage.removeItem("postLoginCallback");
        // `/` decides the landing from the session: the organization for a
        // member, the expert area for everyone else.
        const target = safeInternalPath(stored, "/");
        window.location.href = `/two-factor-auth?mode=verify&returnUrl=${encodeURIComponent(target)}`;
      },
    }),
    ssoClient(),
    apiKeyClient(),
    magicLinkClient(),
    oneTapClient({
      // Empty fallback keeps Better Auth's plugin type inference intact;
      // the GoogleOneTap component skips the call when the env var is unset.
      clientId: googleOneTapClientId!,
      autoSelect: false,
      cancelOnTapOutside: true,
      context: "signin",
      promptOptions: {
        baseDelay: 1000,
        maxAttempts: 5,
        fedCM: true,
      },
    }),
    lastLoginMethodClient(),
  ],
});

export const { signIn, useSession } = authClient;

// Clears the view-as impersonation cookie so the next login starts as the
// real role. Call sites must use this wrapper, not authClient.signOut.
export const signOut: typeof authClient.signOut = (...args) => {
  clearViewAs();
  return authClient.signOut(...args);
};
